// import store from '@/store'
// import request from '@/utils/request'

export const rotateImage = (imgSrc) => {
    return new Promise((resolve, reject) => {
      const image = document.createElement("img");
      image.crossOrigin = "anonymous";
      image.src = imgSrc;
  
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
  
        if (image.width > image.height) {
          canvas.width = image.height;
          canvas.height = image.width;
          ctx.translate(image.height, image.width / image.height);
        } else {
          canvas.height = image.width;
          canvas.width = image.height;
          ctx.translate(image.height, image.width / image.height);
        }
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(image, 0, 0);
  
        resolve(canvas.toDataURL()); // ✅ return correctly
      };
  
      image.onerror = reject;
    });
  };
// src/utils/utils.js

/** ------------------------
 *  Small pure helpers
 *  ------------------------ */
export const formatDate = (s) => {
    if (!s) return '';
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split('.');
      s = `${yyyy}-${mm}-${dd}`;
    }
    const d = new Date(s);
    if (isNaN(d)) return s;
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  export const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };
  
  export const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  
  /** ------------------------
   *  1) updateOriginalFileName
   *  ------------------------ */
  // Keep it framework-agnostic by passing store + key
  export const updateOriginalFileName = (store, originalFileNameKey, newValue) => {
    store.commit('updateField', {
      key: originalFileNameKey,
      value: newValue,
    });
  };
  
  /** ------------------------
   *  2) crop() - split into a pure helper
   *      -> returns { file, imageDataUrl }
   *  ------------------------ */
  export const cropToFile = ({ cropperRef, imageDataUrl, fileType, selectedFileName, b64toBlobFn = b64toBlob }) => {
    if (fileType !== 'application/pdf') {
      const result = cropperRef.getResult();
      // Use actual file type for compression instead of hardcoded JPEG
      // Default to image/jpeg if type is not available or not an image
      const outputType = fileType && fileType.startsWith('image/') ? fileType : 'image/jpeg';
      imageDataUrl = result.canvas.toDataURL(outputType);
    }

    // dataURL -> Blob -> File
    const block = imageDataUrl.split(';');
    const contentType = block[0].split(':')[1];
    const realData = block[1].split(',')[1];
    const blob = b64toBlobFn(realData, contentType);
    const file = new File([blob], selectedFileName);

    return { file, imageDataUrl };
  };
  
  /** ------------------------
   *  3) uploadImage(file) helpers
   *  ------------------------ */
  export const validateFileType = (file, allowed = ['image/jpeg','image/jpg','image/png','application/pdf']) =>
    !!file && allowed.includes(file.type);
  
  export const prepareUploadImage = async (file) => {
    // returns { dataUrl, type, name }
    const dataUrl = await readFileAsDataURL(file);
    return { dataUrl, type: file.type, name: file.name };
  };
  
  /** ------------------------
   *  4) onFileInput(file) split into two:
   *      a) encryptFile
   *      b) uploadEncryptedFile
   *  ------------------------ */
  export const encryptFile = (file, { CryptoJS, encryptFn /* this.$encrypt */ }) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const wordArray = CryptoJS.lib.WordArray.create(reader.result);
        const encrypted = encryptFn(wordArray, false); // OpenSSL-format base64 string
        const fileEnc = new Blob([encrypted]); // blob of encrypted text
        const out = new File([fileEnc], `${file.name}.enc`, { type: fileEnc.type });
        resolve(out);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  
  export const uploadEncryptedFile = async (request, file, fieldType = 'identity', dataSegmentId = null) => {
    try{
    const formData = new FormData();
    formData.append('dataSegmentId', dataSegmentId);
    formData.append('type', fieldType);
    formData.append('file', file, file.name);
    const headers = { 'Content-Type': 'multipart/form-data' };
    const { data } = await request.post('files/encrypt', formData, { headers });
    return data;
  } catch (error) {
    console.log('error from uploadEncryptedFile',error)
      return {
        success: false,
        message: error
      }
  }
  };
  
  /** ------------------------
   *  5) fileCompressor(input) -> returns a File (compressed or original)
   *  ------------------------ */
  export const compressImageIfNeeded = (file, { maxWidth = 600, thresholdBytes = 2097152 } = {}) =>
    new Promise((resolve, reject) => {
      if (!file || file.size < thresholdBytes) return resolve(file);

      const fr = new FileReader();
      fr.onload = (event) => {
        const img = document.createElement('img');
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const scale = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * scale;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Use actual file type instead of hardcoded JPEG
          const outputType = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
          const dataUrl = canvas.toDataURL(outputType);
          const [head, body] = dataUrl.split(',');
          const mime = head.match(/:(.*?);/)[1];
          const bstr = atob(body);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) u8arr[n] = bstr.charCodeAt(n);
          resolve(new File([u8arr], file.name, { type: mime }));
        };
        img.onerror = reject;
      };
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
    